/**
 * Financial Page Init
 *
 * Events triggered on the table elements are used for communication between
 * the different components.
 *
 * 'datachange' signals a change in the actual data being displayed.
 * 'htmlchange' signals a change in the presentation.
 *
 * @module
 */

import { $$ } from '../../utilities/QuerySelectors.js';
import { FOOL_GLOBALS, isTrue } from '../../utilities/WindowGlobals.js';
import { addFallbackSources } from '../../utilities/Image.js';
import { SortableTable } from '../reusable/tables/SortableTable.js';
import { FixedSymbolTable } from '../reusable/tables/FixedSymbolTable.js';
import { ExpandableTable } from '../reusable/tables/ExpandableTable.js';
import { Chart } from './Chart.js';
import { PriceChangeMarkers } from './PriceChangeMarkers.js';
import { TableTooltips } from './TableTooltips.js';
import { init as initFundamentals } from './ticker/Fundamentals.js';
import { init as initAccordions } from './Accordions.js';
import { init as initPagination } from './Pagination.js';

/**
 * Financial tables + pagination features.
 *
 * @private
 * @return {void}
 */
function initEnhancements() {
    $$('.financial-table, .morningstar-table').forEach((t) => {
        new SortableTable(t).init();
        new FixedSymbolTable(t).init();

        const expandable = new ExpandableTable(t);
        if (t.querySelector('[data-quoteapi]')) {
            expandable.setConstantDelay(2000);
        }
        expandable.init();
    });

    initPagination();

    if (isTrue(FOOL_GLOBALS.FOOL_FREESITE.hasQuoteApi)) {
        displayErrorMessagesWhenMissingAsxData();
    }
}

/**
 * QuoteAPI can have trouble getting market-wide data right
 * at the start of the market open, i.e. 7 AM Sydney time.
 *
 * @see https://fool.atlassian.net/browse/GT-1262
 *
 * @private
 * @return {void}
 */
function displayErrorMessagesWhenMissingAsxData() {
    // few rows means that the API did not fill up the table.
    $$('.market-movers')
        .filter((section) => section.querySelectorAll('tbody tr').length < 5)
        .forEach((section) => section.classList.add('no-data'));
}

/**
 * Company logo at the top of the ticker page.
 *
 * @private
 * @return {void}
 */
function addCompanyLogo() {
    const t = FOOL_GLOBALS.FOOL_FREESITE.ticker;
    const img = document.createElement('img');
    const baseUrl = 'https://g.foolcdn.com/art/companylogos/medium';

    img.addEventListener('load', () => img.classList.add('loaded'));
    img.classList.add('company-logo');
    img.src = `${baseUrl}/${t.exchange}:${t.symbol}.png`;
    addFallbackSources(img, [`${baseUrl}/${t.symbol}.png`, `${baseUrl}/${t.exchange}_${t.symbol}.png`]);
    img.alt = t.name + ' Logo';
    document.querySelector('.entry-title').appendChild(img);
}

/**
 * Is the current page showing a company announcement?
 *
 * @private
 * @return {boolean}
 */
function isCompanyAnnouncementPage() {
    return document.body.className.indexOf('company-announcements') !== -1;
}

/**
 * Entry point for enhancements to the financial templates.
 *
 * 1) Make tables sortable by clicks on the headings.
 * 2) Keep the symbol fixed when horizontally scrolling.
 * 3) Enable "Show more" buttons to keep tables compact.
 * 4) Make all sections collapsible for mobile users.
 *
 * @return {void}
 */
export function init() {
    const mainContainer = document.querySelector('#main-container');

    if (isCompanyAnnouncementPage()) {
        if (mainContainer) {
            mainContainer.classList.add('quoteapi-ready');
        }
        return;
    }

    new PriceChangeMarkers().init();

    $$('.financial-chart').forEach((n) => new Chart(n).init());

    if (isTrue(FOOL_GLOBALS.FOOL_FREESITE.isTickerPage) && !isTrue(FOOL_GLOBALS.FOOL_FREESITE.hasQuoteMediaApi)) {
        addCompanyLogo();
    }

    initAccordions();
    initFundamentals();
    new TableTooltips().init();

    $$('[data-quoteapi="dateTime"]').forEach((date) => (date.innerHTML = date.innerHTML.replace('as at', 'As at')));

    // quoteapi compatible price movement
    $$('.price-change-dollars').forEach((pcd) => {
        pcd.classList.add('movement');
        pcd.insertAdjacentHTML('afterbegin', `<h5>${FOOL_GLOBALS.FOOL_FREESITE.tickerMovementLabel}</h5>`);
    });

    if (mainContainer) {
        mainContainer.classList.add('quoteapi-ready');
    }

    if (isTrue(FOOL_GLOBALS.FOOL_FREESITE.hasQuoteMediaApi)) {
        if (mainContainer) {
            mainContainer.classList.remove('quoteapi-ready');
            mainContainer.classList.add('quotemediaapi-ready');
        }
    }

    setTimeout(initEnhancements, 100);

    setTimeout(function () {
        // remove button styles from QuoteMedia widgets
        document.querySelectorAll('.qmod-btn').forEach((el) => el.classList.add('no-btn-style'));
    }, 5000);
}
